import React, { createContext, useState, useEffect, useContext } from 'react';

export const CurrencyContext = createContext();

const EXCHANGE_RATES = {
  EUR: 1,
  USD: 1.09,
  GBP: 0.86,
  JPY: 164.5,
  AUD: 1.68,
  CAD: 1.52,
  CHF: 0.95,
  CNY: 7.89
};

const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥'
};

export const CurrencyProvider = ({ children, user }) => {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('currency') || 'EUR';
  });

  // Sync with user's database preference when user data is available
  useEffect(() => {
    if (user?.currency) {
      setCurrency(user.currency);
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  const convertAmount = (amount, fromCurrency = 'EUR') => {
    if (!amount) return 0;
    
    // Safe fallback if currency not found
    const fromRate = EXCHANGE_RATES[fromCurrency] || EXCHANGE_RATES['EUR'];
    const toRate = EXCHANGE_RATES[currency] || EXCHANGE_RATES['EUR'];
    
    const amountInEUR = amount / fromRate;
    return amountInEUR * toRate;
  };

  const formatPrice = (amount, fromCurrency = 'EUR', showSymbol = true, decimals = 2) => {
    try {
      const converted = convertAmount(amount, fromCurrency);
      const formatted = converted.toFixed(decimals);
      const symbol = CURRENCY_SYMBOLS[currency] || CURRENCY_SYMBOLS['EUR'];
      return showSymbol ? `${symbol}${formatted}` : formatted;
    } catch (error) {
      console.error('Error formatting price:', error);
      return `${amount.toFixed(decimals)}`;
    }
  };

  const getCurrencySymbol = () => CURRENCY_SYMBOLS[currency];

  const value = {
    currency,
    setCurrency,
    convertAmount,
    formatPrice,
    getCurrencySymbol,
    availableCurrencies: Object.keys(EXCHANGE_RATES),
    currencySymbols: CURRENCY_SYMBOLS
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
